/**
 * EventParticipantsManager
 *
 * Business logic manager for event participant operations.
 * Handles creation, validation, and manipulation of event participants
 * (organisers, invited guests, and family member labels).
 */

import type { EventParticipant, EventInvitee } from '@/types/events';
import type { FamilyMember } from '@/types/family-member';

const EMAIL_REGEX = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

export class EventParticipantsManager {
  /**
   * Build participants from legacy invitees + family member IDs
   * Used for backward compatibility when migrating old events
   */
  static buildParticipantsFromLegacy(
    invitees: EventInvitee[],
    familyMemberIds: string[],
    familyMembers: FamilyMember[],
    currentUserId: string | undefined,
  ): EventParticipant[] {
    const participants: EventParticipant[] = [];

    // Add organiser if current user exists and is in invitees as "self"
    const selfInvitee = invitees.find(inv => inv.isSelf);
    if (selfInvitee) {
      participants.push({
        id: selfInvitee.id,
        role: 'organiser',
        name: selfInvitee.name,
        email: selfInvitee.email,
        avatarUrl: selfInvitee.avatarUrl,
        isSelf: true,
      });
    } else if (currentUserId) {
      // Auto-add current user as organiser if not in legacy invitees
      participants.push({
        id: `organiser-${currentUserId}`,
        role: 'organiser',
        name: undefined,
        email: undefined,
        avatarUrl: null,
        isSelf: true,
      });
    }

    // Add invited attendees (excluding self)
    invitees.forEach(inv => {
      if (!inv.isSelf) {
        participants.push({
          id: inv.id,
          role: 'invited',
          name: inv.name,
          email: inv.email,
          avatarUrl: inv.avatarUrl,
          isSelf: false,
        });
      }
    });

    // Add family member labels
    familyMemberIds.forEach(familyId => {
      const familyMember = familyMembers.find(fm => fm.id === familyId);
      participants.push({
        id: `label-${familyId}`,
        role: 'label',
        familyMemberId: familyId,
        familyMemberName: familyMember?.name,
        familyMemberColor: familyMember?.color,
      });
    });

    return participants;
  }

  /**
   * Add organiser to participants list
   */
  static addOrganiser(
    participants: EventParticipant[],
    userId: string,
    name?: string,
    email?: string,
    avatarUrl?: string | null,
  ): EventParticipant[] {
    // Check if organiser already exists
    const hasOrganiser = participants.some(p => p.role === 'organiser');
    if (hasOrganiser) {
      return participants;
    }

    return [
      {
        id: `organiser-${userId}`,
        role: 'organiser',
        name,
        email,
        avatarUrl: avatarUrl ?? null,
        isSelf: true,
      },
      ...participants,
    ];
  }

  /**
   * Add email invitee to participants list
   * Returns structured result with error message if validation fails
   */
  static addEmailInvitee(
    participants: EventParticipant[],
    email: string,
    currentUserId: string | undefined,
  ): { participants: EventParticipant[]; error?: string } {
    // Validate email
    if (!EMAIL_REGEX.test(email)) {
      return {
        participants,
        error: 'Invalid email address. Please enter a valid email.',
      };
    }

    // Check if email already exists
    const emailExists = participants.some(
      p => p.email && p.email.toLowerCase() === email.toLowerCase(),
    );
    if (emailExists) {
      return {
        participants,
        error: 'This email has already been added.',
      };
    }

    const newInvitee: EventParticipant = {
      id: `invited-${Date.now()}`,
      role: 'invited',
      name: email.split('@')[0], // Use email prefix as name
      email,
      avatarUrl: null,
      isSelf: false,
    };

    return { participants: [...participants, newInvitee] };
  }

  /**
   * Remove participant from list
   * Cannot remove organiser
   */
  static removeParticipant(
    participants: EventParticipant[],
    id: string,
  ): EventParticipant[] {
    // Find participant
    const participant = participants.find(p => p.id === id);
    if (!participant) {
      return participants;
    }

    // Cannot remove organiser
    if (participant.role === 'organiser' && participant.isSelf) {
      return participants;
    }

    return participants.filter(p => p.id !== id);
  }

  /**
   * Toggle family member label
   * Adds if not present, removes if present
   */
  static toggleFamilyLabel(
    participants: EventParticipant[],
    familyMemberId: string,
    familyMember: FamilyMember,
  ): EventParticipant[] {
    // Check if label already exists
    const existingIndex = participants.findIndex(
      p => p.role === 'label' && p.familyMemberId === familyMemberId,
    );

    if (existingIndex >= 0) {
      // Remove label
      return participants.filter((_, index) => index !== existingIndex);
    } else {
      // Add label
      const newLabel: EventParticipant = {
        id: `label-${familyMemberId}`,
        role: 'label',
        familyMemberId,
        familyMemberName: familyMember.name,
        familyMemberColor: familyMember.color,
      };
      return [...participants, newLabel];
    }
  }

  /**
   * Add "Me" as a label participant if not already present
   */
  static addMeLabel(
    participants: EventParticipant[],
    meMember: FamilyMember,
  ): EventParticipant[] {
    const hasMe = participants.some(
      p => p.role === 'label' && p.familyMemberId === meMember.id,
    );
    if (hasMe) return participants;

    return [
      ...participants,
      {
        id: `label-${meMember.id}`,
        role: 'label',
        familyMemberId: meMember.id,
        familyMemberName: meMember.name,
        familyMemberColor: meMember.color,
      },
    ];
  }

  /**
   * Count "For" labels (role === 'label') in participants
   */
  static countForLabels(participants: EventParticipant[]): number {
    return participants.filter(p => p.role === 'label').length;
  }

  /**
   * Unified toggle for the "For" section.
   * Only manages labels — invited guests are a separate concern.
   * Returns { blocked: true } if deselecting would leave 0 active labels.
   * meIsActive counts toward the min-1 rule (Me is not a participant label).
   */
  static toggleForMember(
    participants: EventParticipant[],
    familyMember: FamilyMember,
    meIsActive: boolean = false,
  ): { participants: EventParticipant[]; blocked: boolean } {
    const isSelected = participants.some(
      p => p.role === 'label' && p.familyMemberId === familyMember.id,
    );

    if (isSelected) {
      // Deselecting — check minimum-1 rule (labels + Me)
      const totalActive = this.countActiveLabelsWithMe(participants, meIsActive);
      if (totalActive <= 1) {
        return { participants, blocked: true };
      }

      const updated = participants.filter(
        p => !(p.role === 'label' && p.familyMemberId === familyMember.id),
      );

      return { participants: updated, blocked: false };
    } else {
      // Selecting — add label
      const newLabel: EventParticipant = {
        id: `label-${familyMember.id}`,
        role: 'label',
        familyMemberId: familyMember.id,
        familyMemberName: familyMember.name,
        familyMemberColor: familyMember.color,
      };

      return { participants: [...participants, newLabel], blocked: false };
    }
  }

  /**
   * Count total active labels including Me (which is not a participant).
   * Used for min-1 validation across both participant labels and Me toggle.
   */
  static countActiveLabelsWithMe(
    participants: EventParticipant[],
    meIsActive: boolean,
  ): number {
    return this.countForLabels(participants) + (meIsActive ? 1 : 0);
  }

  /**
   * For edit mode: ensure calendar-linked invitees also have a corresponding label.
   * If a member's calendar email appears in the invite list, add their label too.
   */
  static syncLabelsFromInvites(
    participants: EventParticipant[],
    familyMembers: FamilyMember[],
    memberCalendarEmails: Map<string, string>,
  ): EventParticipant[] {
    let updated = [...participants];

    for (const [memberId, email] of memberCalendarEmails) {
      const hasInvite = participants.some(
        p => (p.role === 'invited' || p.role === 'organiser') &&
          p.email?.toLowerCase() === email.toLowerCase(),
      );
      if (!hasInvite) continue;

      const hasLabel = participants.some(
        p => p.role === 'label' && p.familyMemberId === memberId,
      );
      if (hasLabel) continue;

      const member = familyMembers.find(fm => fm.id === memberId);
      if (!member) continue;

      updated.push({
        id: `label-${memberId}`,
        role: 'label',
        familyMemberId: memberId,
        familyMemberName: member.name,
        familyMemberColor: member.color,
      });
    }

    return updated;
  }

  /**
   * Get family member IDs from participants
   * Used for serializing to description field
   */
  static getFamilyMemberIds(participants: EventParticipant[]): string[] {
    return participants
      .filter(p => p.role === 'label' && p.familyMemberId)
      .map(p => p.familyMemberId!);
  }

  /**
   * Validate participants list
   */
  static validate(participants: EventParticipant[]): { valid: boolean; errors: string[] } {
    const errors: string[] = [];

    // Check for at least one organiser
    const hasOrganiser = participants.some(p => p.role === 'organiser');
    if (!hasOrganiser) {
      errors.push('Event must have an organiser');
    }

    // Validate invited participants have emails
    const invitedWithoutEmail = participants.some(
      p => p.role === 'invited' && !p.email,
    );
    if (invitedWithoutEmail) {
      errors.push('All invited participants must have an email address');
    }

    // Validate family labels have member IDs
    const labelsWithoutId = participants.some(
      p => p.role === 'label' && !p.familyMemberId,
    );
    if (labelsWithoutId) {
      errors.push('All family labels must have a member ID');
    }

    return {
      valid: errors.length === 0,
      errors,
    };
  }

  /**
   * Check if participant can be removed
   */
  static canRemove(participant: EventParticipant): boolean {
    // Cannot remove self organiser
    if (participant.role === 'organiser' && participant.isSelf) {
      return false;
    }
    return true;
  }

  /**
   * Get display name for participant
   */
  static getDisplayName(participant: EventParticipant): string {
    if (participant.role === 'organiser') {
      return participant.name ?? participant.email ?? 'Organiser';
    } else if (participant.role === 'invited') {
      return participant.name ?? participant.email ?? 'Guest';
    } else if (participant.role === 'label') {
      return participant.familyMemberName ?? 'Family Member';
    }
    return 'Unknown';
  }
}
